import { useState, useRef, useEffect, useMemo } from "react";
import {
  Dialog,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import { MyLocation, Search, Close, Save } from "@mui/icons-material";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import { fetchCustomerPage } from "../../api/CustomerService";
import ZoneSelect from "../common/ZoneSelect";
import CitySelect from "../common/CitySelect";
import customerLabel from "../../utils/customerLabel";
import AsyncAutocomplete from "../common/AsyncAutocomplete";

// Leaflet icon setup
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Component to handle map clicks / drag
function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
    },
  });

  const markerRef = useRef(null);
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          setPosition(marker.getLatLng());
        }
      },
    }),
    [setPosition],
  );

  return position === null ? null : (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
    ></Marker>
  );
}

// Map center automatic update if search changes it
function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, map, zoom]);
  return null;
}

export const CreateProjectDialog = ({
  open,
  onClose,
  saving,
  onSave,
  errors = {},
}) => {
  const [project, setProject] = useState({
    project_id: "",
    project_name: "",
    customer_id: "",
    zone_id: "",
    city_id: "",
    lift_type: "",
    cabin_type: "",
    capacity_persons: "",
    capacity_kg: "",
    no_of_floors: "",
    no_of_basements: "",
    shaft_length: "",
    shaft_width: "",
    door_type: "manual",
    is_swing_door: false,
    vision_type: "",
    lift_configuration: "mr",
    additional_notes: "",
    status: "under_construction",
  });

  const [position, setPosition] = useState(null); // {lat, lng}
  const [mapCenter, setMapCenter] = useState([17.385, 78.4867]); // Default Hyderabad
  const [mapZoom, setMapZoom] = useState(12);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      setProject({
        project_id: "",
        project_name: "",
        customer_id: "",
        zone_id: "",
        city_id: "",
        lift_type: "",
        cabin_type: "",
        capacity_persons: "",
        capacity_kg: "",
        no_of_floors: "",
        no_of_basements: "",
        shaft_length: "",
        shaft_width: "",
        door_type: "manual",
        is_swing_door: false,
        lift_configuration: "mr",
        additional_notes: "",
        status: "under_construction",
      });
      setPosition(null);
      setSearchQuery("");
      setMapCenter([17.385, 78.4867]);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Vision type only applies to automatic doors; swing only to manual doors.
    if (name === "door_type") {
      setProject({
        ...project,
        door_type: value,
        vision_type: value === "automatic" ? project.vision_type : "",
        is_swing_door: value === "manual" ? project.is_swing_door : false,
      });
    } else if (name === "lift_type") {
      // Goods lifts are quoted by load (kg), not persons.
      setProject({
        ...project,
        lift_type: value,
        capacity_persons: value === "goods" ? "" : project.capacity_persons,
      });
    } else {
      setProject({ ...project, [name]: value });
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery,
        )}`,
      );
      const data = await resp.json();
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const newPos = { lat: parseFloat(lat), lng: parseFloat(lon) };
        setMapCenter([newPos.lat, newPos.lng]);
        setMapZoom(15);
        setPosition(newPos);
      } else {
        toast.warning("Location not found");
      }
    } catch (err) {
      toast.error("Geocoding failed");
    }
  };

  const handleGetCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const newPos = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          setMapCenter([newPos.lat, newPos.lng]);
          setMapZoom(16);
          setPosition(newPos);
        },
        () => {
          toast.error(
            "Failed to retrieve location. Please check browser permissions.",
          );
        },
        { enableHighAccuracy: true },
      );
    } else {
      toast.error("Geolocation is not supported by this browser.");
    }
  };

  const submit = () => {
    const payload = { ...project };
    if (position) {
      payload.latitude = position.lat;
      payload.longitude = position.lng;
    }
    onSave(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={!saving ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.dialog.borderRadius,
          minWidth: theme.dialog.minWidth,
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight={"bold"}
            color={theme.palette.primary.main}
          >
            New Project
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={!saving ? onClose : undefined} disabled={saving}>
            <Close />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ p: 4 }}>
        {/* Row 1: Project ID + Project Name + Customer */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="Project ID"
            name="project_id"
            value={project.project_id}
            onChange={handleChange}
            disabled={saving}
            error={!!errors.project_id}
            helperText={errors.project_id?.[0]}
          />
          <TextField
            fullWidth
            label="Project Name"
            name="project_name"
            value={project.project_name}
            onChange={handleChange}
            disabled={saving}
            error={!!errors.project_name}
            helperText={errors.project_name?.[0]}
          />
          <AsyncAutocomplete
            fullWidth
            size="medium"
            label="Customer"
            placeholder="Search customer..."
            value={project.customer_id}
            onChange={(id) =>
              setProject((p) => ({ ...p, customer_id: id }))
            }
            fetchPage={fetchCustomerPage}
            getOptionLabel={customerLabel}
            disabled={saving}
            error={!!errors.customer_id}
            helperText={errors.customer_id?.[0]}
          />
        </Box>

        {/* Row 2: Zone + City */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <ZoneSelect
            fullWidth
            value={project.zone_id}
            onChange={(id) =>
              setProject((p) => ({ ...p, zone_id: id, city_id: "" }))
            }
            disabled={saving}
            error={!!errors.zone_id}
            helperText={errors.zone_id?.[0]}
          />
          <CitySelect
            fullWidth
            zoneId={project.zone_id}
            value={project.city_id}
            onChange={(id) => setProject((p) => ({ ...p, city_id: id }))}
            disabled={saving}
            error={!!errors.city_id}
            helperText={
              errors.city_id?.[0] ||
              (!project.zone_id ? "Select a zone first" : "")
            }
          />
        </Box>

        {/* Row 3: Status (full width) */}
        <TextField
          select
          fullWidth
          label="Status"
          name="status"
          value={project.status}
          onChange={handleChange}
          disabled={saving}
          error={!!errors.status}
          helperText={errors.status?.[0]}
          sx={{ mb: 2 }}
        >
          <MenuItem value="under_construction">Under Construction</MenuItem>
          <MenuItem value="handed_over">Handed Over</MenuItem>
          <MenuItem value="under_service_plan">Under Service Plan</MenuItem>
        </TextField>

        {/* Row 4: Lift Type + Cabin Type */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            select
            fullWidth
            label="Lift Type"
            name="lift_type"
            value={project.lift_type}
            onChange={handleChange}
            disabled={saving}
          >
            <MenuItem value="passenger">Passenger</MenuItem>
            <MenuItem value="freight">Freight</MenuItem>
            <MenuItem value="home">Home</MenuItem>
            <MenuItem value="hospital">Hospital</MenuItem>
            <MenuItem value="goods">Goods</MenuItem>
            <MenuItem value="stretcher">Stretcher</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Cabin Type"
            name="cabin_type"
            value={project.cabin_type}
            onChange={handleChange}
            disabled={saving}
          />
        </Box>

        {/* Row 5: Door Type + Swing (manual) / Vision Type (automatic) */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
          <TextField
            select
            fullWidth
            label="Door Type"
            name="door_type"
            value={project.door_type}
            onChange={handleChange}
            disabled={saving}
          >
            <MenuItem value="manual">Manual</MenuItem>
            <MenuItem value="automatic">Automatic</MenuItem>
          </TextField>
          {project.door_type === "manual" && (
            <FormControlLabel
              sx={{ minWidth: 120 }}
              control={
                <Checkbox
                  checked={!!project.is_swing_door}
                  onChange={(e) =>
                    setProject({ ...project, is_swing_door: e.target.checked })
                  }
                  disabled={saving}
                />
              }
              label="Swing"
            />
          )}
          {project.door_type === "automatic" && (
            <TextField
              select
              fullWidth
              label="Vision Type"
              name="vision_type"
              value={project.vision_type || ""}
              onChange={handleChange}
              disabled={saving}
            >
              <MenuItem value="big_vision">Big Vision</MenuItem>
              <MenuItem value="small_vision">Small Vision</MenuItem>
              <MenuItem value="half_vision">Half Vision</MenuItem>
              <MenuItem value="no_vision">No Vision</MenuItem>
            </TextField>
          )}
        </Box>

        {/* Row 6: Capacity (Persons + Kg) */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          {project.lift_type !== "goods" && (
            <TextField
              fullWidth
              type="number"
              label="Capacity (Persons)"
              name="capacity_persons"
              value={project.capacity_persons}
              onChange={handleChange}
              disabled={saving}
            />
          )}
          <TextField
            fullWidth
            type="number"
            label="Capacity (Kg)"
            name="capacity_kg"
            value={project.capacity_kg}
            onChange={handleChange}
            disabled={saving}
          />
        </Box>

        {/* Row 7: Floors + Basements + Configuration */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            type="number"
            label="No. of Floors"
            name="no_of_floors"
            value={project.no_of_floors}
            onChange={handleChange}
            disabled={saving}
          />
          <TextField
            fullWidth
            type="number"
            label="No. of Basements"
            name="no_of_basements"
            value={project.no_of_basements}
            onChange={handleChange}
            disabled={saving}
          />
          <TextField
            select
            fullWidth
            label="Configuration"
            name="lift_configuration"
            value={project.lift_configuration}
            onChange={handleChange}
            disabled={saving}
          >
            <MenuItem value="mr">MR</MenuItem>
            <MenuItem value="semi-mrl">Semi-MRL</MenuItem>
            <MenuItem value="mrl">MRL</MenuItem>
            <MenuItem value="hydraulic">Hydraulic</MenuItem>
            <MenuItem value="belt">Belt</MenuItem>
          </TextField>
        </Box>

        {/* Map Section */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Project Location (Coordinates)
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search location (e.g. Hyderabad)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<Search />}
          >
            Search
          </Button>
          <Tooltip title="Use Current Location">
            <IconButton
              color="primary"
              onClick={handleGetCurrentLocation}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <MyLocation />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Click on the map or drag the marker to precisely set the project
          location.
        </Typography>
        <Box
          sx={{
            height: 300,
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #ccc",
          }}
        >
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: "100%", width: "100%" }}
          >
            <ChangeView center={mapCenter} zoom={mapZoom} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LocationMarker position={position} setPosition={setPosition} />
          </MapContainer>
        </Box>
        <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
          Selected Coordinates:{" "}
          {position
            ? `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}`
            : "None"}
        </Typography>
      </Box>
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          disabled={saving}
          sx={{
            backgroundColor: theme.button.backgroundColor,
            color: "#000",
            borderRadius: theme.button.borderRadius,
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={saving}
          sx={{
            backgroundColor: theme.button.editBackgroundColor,
            borderRadius: theme.button.borderRadius,
          }}
          startIcon={<Save />}
        >
          {saving ? "Creating..." : "Save Project"}
        </Button>
      </Box>
    </Dialog>
  );
};
